/**
 * Draggable Logo Cloud Marquee Component
 *
 * Creates an infinite horizontal scrolling animation for logo clouds with drag interaction.
 * Based on Osmo Supply's draggable marquee implementation.
 * Uses GSAP, Observer, and ScrollTrigger for smooth, performant animation.
 *
 * Features:
 * - Seamless infinite loop
 * - Drag/swipe to control speed and direction
 * - Velocity-based momentum with smooth transitions
 * - Automatic pause when out of viewport
 * - GPU-accelerated performance
 *
 * Usage:
 * Add data attributes to HTML:
 * <div data-draggable-marquee-init data-direction="left" data-duration="25">
 *   <div data-draggable-marquee-collection>
 *     <div data-draggable-marquee-list>
 *       <!-- Logo items -->
 *     </div>
 *   </div>
 * </div>
 */

import gsap from "gsap";
import { Observer } from "gsap/Observer";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register GSAP plugins
gsap.registerPlugin(Observer, ScrollTrigger);

/**
 * Initialize all draggable marquee instances on the page
 */
export function initDraggableMarquee() {
  const wrappers = document.querySelectorAll("[data-draggable-marquee-init]");

  const getNumberAttr = (el, name, fallback) => {
    const value = parseFloat(el.getAttribute(name));
    return Number.isFinite(value) ? value : fallback;
  };

  wrappers.forEach((wrapper) => {
    if (wrapper.getAttribute("data-draggable-marquee-init") === "initialized")
      return;

    const collection = wrapper.querySelector(
      "[data-draggable-marquee-collection]",
    );
    const list = wrapper.querySelector("[data-draggable-marquee-list]");
    if (!collection || !list) return;

    const duration = getNumberAttr(wrapper, "data-duration", 20);
    const multiplier = getNumberAttr(wrapper, "data-multiplier", 40);
    const sensitivity = getNumberAttr(wrapper, "data-sensitivity", 0.01);

    const wrapperWidth = wrapper.getBoundingClientRect().width;
    const listWidth = list.scrollWidth || list.getBoundingClientRect().width;
    if (!wrapperWidth || !listWidth) return;

    // Make enough duplicates to cover screen
    const minRequiredWidth = wrapperWidth + listWidth + 2;
    while (collection.scrollWidth < minRequiredWidth) {
      const listClone = list.cloneNode(true);
      listClone.setAttribute("data-draggable-marquee-clone", "");
      listClone.setAttribute("aria-hidden", "true");
      collection.appendChild(listClone);
    }

    const wrapX = gsap.utils.wrap(-listWidth, 0);

    gsap.set(collection, { x: 0 });

    const marqueeLoop = gsap.to(collection, {
      x: -listWidth,
      duration,
      ease: "none",
      repeat: -1,
      onReverseComplete: () => marqueeLoop.progress(1),
      modifiers: {
        x: (x) => wrapX(parseFloat(x)) + "px",
      },
    });

    // Direction can be used for css + set initial direction on load
    const initialDirectionAttr = (
      wrapper.getAttribute("data-direction") || "left"
    ).toLowerCase();
    const baseDirection = initialDirectionAttr === "right" ? -1 : 1;

    const timeScale = { value: 1 };

    timeScale.value = baseDirection;
    wrapper.setAttribute(
      "data-direction",
      baseDirection < 0 ? "right" : "left",
    );

    if (baseDirection < 0) marqueeLoop.progress(1);

    function applyTimeScale() {
      marqueeLoop.timeScale(timeScale.value);
      wrapper.setAttribute(
        "data-direction",
        timeScale.value < 0 ? "right" : "left",
      );
    }

    applyTimeScale();

    // Drag observer
    const marqueeObserver = Observer.create({
      target: wrapper,
      type: "pointer,touch",
      preventDefault: true,
      debounce: false,
      onChangeX: (observerEvent) => {
        let velocityTimeScale = observerEvent.velocityX * -sensitivity;
        velocityTimeScale = gsap.utils.clamp(
          -multiplier,
          multiplier,
          velocityTimeScale,
        );

        gsap.killTweensOf(timeScale);

        const restingDirection = velocityTimeScale < 0 ? -1 : 1;

        gsap
          .timeline({ onUpdate: applyTimeScale })
          .to(timeScale, {
            value: velocityTimeScale,
            duration: 0.1,
            overwrite: true,
          })
          .to(timeScale, { value: restingDirection, duration: 1.0 });
      },
    });

    // Pause marquee when scrolled out of view
    ScrollTrigger.create({
      trigger: wrapper,
      start: "top bottom",
      end: "bottom top",
      onEnter: () => {
        marqueeLoop.resume();
        applyTimeScale();
        marqueeObserver.enable();
      },
      onEnterBack: () => {
        marqueeLoop.resume();
        applyTimeScale();
        marqueeObserver.enable();
      },
      onLeave: () => {
        marqueeLoop.pause();
        marqueeObserver.disable();
      },
      onLeaveBack: () => {
        marqueeLoop.pause();
        marqueeObserver.disable();
      },
    });

    wrapper.setAttribute("data-draggable-marquee-init", "initialized");
  });
}

// Auto-initialize on DOM ready
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initDraggableMarquee);
  } else {
    initDraggableMarquee();
  }
}
